import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import { ROUTES } from 'config/navConfig';

const SECTOR_COLORS = {
  'Renewable Energy': 'bg-amber-100 text-amber-800',
  'Transport': 'bg-blue-100 text-blue-800',
  'Digital Infrastructure': 'bg-purple-100 text-purple-800',
  'Utilities': 'bg-cyan-100 text-cyan-800',
  'Energy': 'bg-amber-100 text-amber-800',
  'Water': 'bg-cyan-100 text-cyan-800',
};

const STAGE_COLORS = {
  'Operational': 'bg-green-100 text-green-800',
  'In Construction': 'bg-orange-100 text-orange-800',
  'Active': 'bg-green-100 text-green-800',
  'Closed': 'bg-slate-100 text-slate-700',
  'Exited': 'bg-red-100 text-red-700',
};

function ESGPill({ score }) {
  const color = score >= 70 ? 'bg-green-100 text-green-800' : score >= 50 ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800';
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${color}`}>{score}</span>;
}

const COLS = [
  { key: 'name', label: 'Asset Name', sortable: true },
  { key: 'sector', label: 'Sector', sortable: true },
  { key: 'region', label: 'Region', sortable: true },
  { key: 'stage', label: 'Stage', sortable: true },
  { key: 'commitment', label: 'Commitment (£m)', sortable: true },
  { key: 'deployed', label: 'Deployed (£m)', sortable: true },
  { key: 'irr', label: 'Net IRR (%)', sortable: true },
  { key: 'moic', label: 'MOIC', sortable: true },
  { key: 'esg', label: 'ESG Score', sortable: true },
  { key: 'nextMilestone', label: 'Next Milestone', sortable: false },
  { key: 'actions', label: '', sortable: false },
];

const AM_MENU_ITEMS = [
  { label: 'Review Pack', icon: 'FileText', path: '/asset-management/review-packs', stateKey: 'assetName' },
  { label: 'Risk Monitor', icon: 'Shield', path: '/asset-management/risk-monitor', stateKey: null },
  { label: 'Watchlist & Actions', icon: 'Eye', path: '/asset-management/watchlist-actions', stateKey: null },
  { label: 'Oversight Hub', icon: 'Users', path: '/asset-management/oversight-hub', stateKey: null },
  { label: 'Value Creation', icon: 'TrendingUp', path: '/asset-management/value-creation', stateKey: null },
];

export default function AssetsTable({ assets, showToast }) {
  const navigate = useNavigate();
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [openMenu, setOpenMenu] = useState(null);

  const handleSort = (key) => {
    if (!COLS?.find(c => c?.key === key)?.sortable) return;
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const sorted = [...assets]?.sort((a, b) => {
    const av = a?.[sortKey], bv = b?.[sortKey];
    if (av === undefined || bv === undefined) return 0;
    const cmp = typeof av === 'string' ? av?.localeCompare(bv) : av - bv;
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const SortIcon = ({ col }) => {
    if (!col?.sortable) return null;
    if (sortKey !== col?.key) return <Icon name="ChevronsUpDown" size={13} color="var(--color-muted-foreground)" />;
    return <Icon name={sortDir === 'asc' ? 'ChevronUp' : 'ChevronDown'} size={13} color="var(--color-primary)" />;
  };

  const navigateToAM = (path, state) => {
    showToast?.('Opening Asset Management view');
    setTimeout(() => navigate(path, { state }), 400);
  };

  const navigateToDetail = (assetId) => {
    navigate(ROUTES.assetDetail.replace(':id', assetId));
  };

  return (
    <div className="card overflow-hidden" style={{ padding: 0 }}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/30">
              {COLS?.map(col => (
                <th
                  key={col?.key}
                  onClick={() => handleSort(col?.key)}
                  className={`px-4 py-3 text-left font-caption text-muted-foreground whitespace-nowrap select-none ${
                    col?.sortable ? 'cursor-pointer hover:text-foreground' : ''
                  }`}
                  style={{ fontSize: '0.7rem', letterSpacing: '0.05em', textTransform: 'uppercase' }}
                >
                  <span className="flex items-center gap-1">
                    {col?.label}
                    <SortIcon col={col} />
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted?.map((asset, idx) => (
              <tr
                key={asset?.id}
                className={`border-b border-border last:border-0 hover:bg-muted/20 cursor-pointer transition-colors duration-100 ${
                  idx % 2 === 0 ? '' : 'bg-muted/5'
                }`}
                onClick={() => navigateToDetail(asset?.id)}
              >
                <td className="px-4 py-3.5">
                  <span className="font-medium text-primary hover:underline">{asset?.name}</span>
                </td>
                <td className="px-4 py-3.5">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${SECTOR_COLORS?.[asset?.sector] || 'bg-gray-100 text-gray-700'}`}>
                    {asset?.sector}
                  </span>
                </td>
                <td className="px-4 py-3.5 text-muted-foreground">{asset?.region}</td>
                <td className="px-4 py-3.5">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${STAGE_COLORS?.[asset?.stage] || 'bg-gray-100 text-gray-700'}`}>
                    {asset?.stage}
                  </span>
                </td>
                <td className="px-4 py-3.5 text-right font-mono text-foreground">{asset?.commitment?.toFixed(1)}</td>
                <td className="px-4 py-3.5 text-right font-mono text-foreground">{asset?.deployed?.toFixed(1)}</td>
                <td className="px-4 py-3.5 text-right">
                  <span className={`font-semibold ${
                    asset?.irr >= 12 ? 'text-green-600' : asset?.irr >= 8 ? 'text-amber-600' : 'text-red-600'
                  }`}>{asset?.irr?.toFixed(1)}%</span>
                </td>
                <td className="px-4 py-3.5 text-right font-mono">{asset?.moic?.toFixed(2)}x</td>
                <td className="px-4 py-3.5 text-center">
                  <ESGPill score={asset?.esg} />
                </td>
                <td className="px-4 py-3.5 text-muted-foreground whitespace-nowrap">{asset?.nextMilestone}</td>
                <td className="px-4 py-3.5" onClick={e => e?.stopPropagation()}>
                  <div className="relative">
                    <button
                      onClick={() => setOpenMenu(openMenu === asset?.id ? null : asset?.id)}
                      className="flex items-center justify-center w-8 h-8 rounded-md hover:bg-muted transition-colors"
                    >
                      <Icon name="MoreHorizontal" size={16} color="var(--color-muted-foreground)" />
                    </button>
                    {openMenu === asset?.id && (
                      <div
                        className="absolute right-0 top-full mt-1 w-52 bg-popover border border-border rounded-lg shadow-lg py-1"
                        style={{ zIndex: 50 }}
                      >
                        {/* View Details */}
                        <button
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors"
                          onClick={() => { navigateToDetail(asset?.id); setOpenMenu(null); }}
                        >
                          <Icon name="Eye" size={14} /> View Details
                        </button>
                        <button
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors"
                          onClick={() => setOpenMenu(null)}
                        >
                          <Icon name="FileDown" size={14} /> Download Report
                        </button>

                        {/* Divider + Asset Management section */}
                        <div className="border-t border-border my-1" />
                        <div className="px-3 py-1">
                          <span className="text-xs font-caption text-muted-foreground uppercase tracking-wide" style={{ fontSize: '0.65rem', letterSpacing: '0.06em' }}>Asset Management</span>
                        </div>
                        {AM_MENU_ITEMS?.map(item => (
                          <button
                            key={item?.label}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:bg-muted transition-colors"
                            onClick={() => {
                              setOpenMenu(null);
                              const state = item?.stateKey === 'assetName'
                                ? { assetId: asset?.id, assetName: asset?.name }
                                : { assetId: asset?.id };
                              navigateToAM(item?.path, state);
                            }}
                          >
                            <Icon name={item?.icon} size={14} color="currentColor" />
                            {item?.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {sorted?.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
          <Icon name="SearchX" size={36} color="var(--color-muted-foreground)" />
          <p className="mt-3 font-caption text-sm">No assets match the selected filters.</p>
        </div>
      )}
    </div>
  );
}
